import {useState, useCallback, useMemo} from "react";
import {
	GridFilterModel,
	GridSortModel,
	GridInitialState,
	GridCallbackDetails
} from "@mui/x-data-grid-pro";
import {
	useListLessons,
	type ListLessonItemDto,
	DEFAULT_LESSONS_LIST_PAGE_SIZE
} from "@/app2/shared/data";
import type {
	LessonsControllerListParams,
	LessonAttributeFilterDto,
	LessonAttributeFilterDtoField,
	LessonAttributeFilterDtoOperator,
	LessonFiltersDtoOperator
} from "@/app2/shared/data/gen";
import {getFromLocalStorage} from "@/utils/common-utilities";

const SORT_FIELDS: readonly string[] = ["title", "videoDuration", "updatedAt", "publishedAt"];

const SORT_STORAGE_KEY = "lessonsSortSettings";

interface PersistedSortModel {
	field: string;
	sort: "asc" | "desc";
}

function getPersistedSort(): PersistedSortModel | null {
	return getFromLocalStorage<PersistedSortModel>(SORT_STORAGE_KEY) ?? null;
}

function persistSort(model: PersistedSortModel): void {
	localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(model));
}

const FILTERABLE_FIELDS: readonly LessonAttributeFilterDtoField[] = [
	"title",
	"videoDuration",
	"updatedAt",
	"publishedAt",
	"languages"
];

interface UseLessonsListGridProps {
	initialFilterSettings: LessonsControllerListParams;
	onFilterModelChange?: (filterModel: GridFilterModel, details: GridCallbackDetails<"filter">) => void;
}

export function useLessonsListGrid({
	initialFilterSettings,
	onFilterModelChange
}: UseLessonsListGridProps): {
		rows: ListLessonItemDto[];
		rowCount: number;
		isLoading: boolean;
		isFetched: boolean;
		refetch: () => void;
		page: number;
		pageSize: number;
		sortModel: GridSortModel;
		onFilterChange: (model: GridFilterModel, details: GridCallbackDetails<"filter">) => void;
		onPageChange: (page: number) => void;
		onPageSizeChange: (pageSize: number) => void;
		onSortModelChange: (model: GridSortModel) => void;
		defaultInitialState: GridInitialState;
	} {
	const persistedSort = getPersistedSort();
	const [filterModel, setFilterModel] = useState<LessonsControllerListParams>(() => ({
		...initialFilterSettings,
		orderBy: persistedSort?.field as LessonsControllerListParams["orderBy"] ?? initialFilterSettings.orderBy,
		orderDirection: persistedSort?.sort ?? initialFilterSettings.orderDirection
	}));

	const {data, isLoading, isFetched, refetch} = useListLessons(filterModel);

	const rows = data?.data ?? [];
	const rowCount = data?.totalCount ?? 0;

	const onPageChange = useCallback((page: number) => {
		setFilterModel((prev) => ({
			...prev,
			page: page + 1
		}));
	}, []);

	const onPageSizeChange = useCallback((pageSize: number) => {
		setFilterModel((prev) => ({
			...prev,
			pageSize,
			page: 1
		}));
	}, []);

	const onFilterChange = useCallback(
		(newFilterModel: GridFilterModel, details: GridCallbackDetails<"filter">) => {
			setFilterModel((prev) => {
				const conditions: LessonAttributeFilterDto[] = (newFilterModel.items ?? [])
					.filter(
						(item: {
							columnField?: string;
							field?: string;
							value?: unknown;
							operatorValue?: string;
							operator?: string;
						}) => {
							const field = item.columnField ?? item.field;
							const hasValue =
								item.value !== undefined &&
								item.value !== null &&
								String(item.value).trim() !== "";
							const operator = item.operatorValue ?? item.operator ?? "co";
							return (
								field &&
								(FILTERABLE_FIELDS as readonly string[]).includes(field) &&
								(operator === "pr" || hasValue)
							);
						}
					)
					.map(
						(item: {
							columnField?: string;
							field?: string;
							value?: unknown;
							operatorValue?: string;
							operator?: string;
						}): LessonAttributeFilterDto => {
							const field = (item.columnField ?? item.field) as LessonAttributeFilterDtoField;
							const operator = (item.operatorValue ?? item.operator ?? "co") as LessonAttributeFilterDtoOperator;
							let value: string | undefined;
							if (operator === "pr") {
								value = undefined;
							} else if (item.value instanceof Date) {
								value = item.value.toISOString();
							} else {
								value = String(item.value ?? "");
							}
							return {field, value, operator};
						}
					);

				onFilterModelChange?.(newFilterModel, details);

				const filtersDto: LessonsControllerListParams["filters"] =
					conditions.length > 0
						? {
							operator: (newFilterModel.linkOperator ?? "and") as LessonFiltersDtoOperator,
							conditions
						}
						: undefined;

				return {
					...prev,
					page: 1,
					filters: filtersDto
				};
			});
		},
		[onFilterModelChange]
	);

	const onSortModelChange = useCallback((newSortModel: GridSortModel) => {
		const field = newSortModel.length > 0 && SORT_FIELDS.includes(newSortModel[0].field)
			? newSortModel[0].field
			: "updatedAt";
		const sort = newSortModel.length > 0 ? (newSortModel[0].sort as "asc" | "desc") : "desc";

		persistSort({field, sort});

		setFilterModel((prev) => ({
			...prev,
			orderBy: field as LessonsControllerListParams["orderBy"],
			orderDirection: sort
		}));
	}, []);

	const sortModel = useMemo<GridSortModel>(
		() => [{
			field: filterModel.orderBy ?? "updatedAt",
			sort: filterModel.orderDirection ?? "desc"
		}],
		[filterModel.orderBy, filterModel.orderDirection]
	);

	const defaultInitialState: GridInitialState = {
		pagination: {
			pageSize: filterModel.pageSize ?? DEFAULT_LESSONS_LIST_PAGE_SIZE,
			page: (filterModel.page ?? 1) - 1
		},
		sorting: {
			sortModel: [{field: filterModel.orderBy ?? "updatedAt", sort: filterModel.orderDirection ?? "desc"}]
		}
	};

	return {
		rows,
		rowCount,
		isLoading,
		isFetched,
		refetch,
		page: (filterModel.page ?? 1) - 1,
		pageSize: filterModel.pageSize ?? DEFAULT_LESSONS_LIST_PAGE_SIZE,
		sortModel,
		onFilterChange,
		onPageChange,
		onPageSizeChange,
		onSortModelChange,
		defaultInitialState
	};
}
